import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Alert,
  Card,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  IconButton,
  TextField, FormHelperText
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { t } from "i18next";
import api from "../../../helpers/api/LoginApi";
import ApiUrls from "../../../helpers/urls/ApiUrls";
import RouteUrls from "../../../helpers/urls/RouteUrls";
import AppConfig from "../../../helpers/constants/AppConfig";
import { setUserPermission } from "../../../redux/actions";
import { appendFormData } from "../../../helpers/functions/form-append";
import MDBox from "components/MDs/MDBox";
import MDTypography from "components/MDs/MDTypography";
import BasicLayout from "components/LayoutContainers/BasicLayout";
import Captcha from "./components/captcha";
import bgImage from "assets/images/sign-in-bg.jpg";

function SignIn({ setUserPermission }) {
  const [formData, setFormData] = useState({
    user: '',
    password: '',
  });
  const { user, password } = formData;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const captchaRef = useRef(null);
  const [checkCaptcha, setCheckCaptcha] = useState(0);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snack, setSnack] = useState({message: '', severity: 'error'});

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleChange = (value, name) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: null
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  };

  const checkInputs = (cpErrs, checkCaptchaCode) => {
    let temp_errors = cpErrs;

    if (user === "") {
      temp_errors.user = t("message.auth_required", { item: t("user.user_id") });
    }
    if (password === "") {
      temp_errors.password = t("message.auth_required", { item: t("auth.password") });
    }

    setErrors(temp_errors);

    if (Object.keys(temp_errors).length === 0) {
      checkCaptchaCode();
    }
  }

  const login = () => {
    let postFormData = appendFormData(formData);

    api.post(ApiUrls.auth.login, postFormData)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          if (response.data.status === "success") {
            localStorage.setItem("token", Date.now());
            localStorage.setItem("user", JSON.stringify(response.data));
            setUserPermission(response.data.perms);
            window.location.href = "/";
          } else {
            if (response.data.message === "user not found" || response.data.message === "user or password incorrect disabled ") {
              showSnackbar("شناسه کاربری یا رمز عبور غلط می‌باشد!");
            } else if (response.data.message === "user disabled") {
              showSnackbar("اکانت کاربری شما غیرفعال شده است، لطفا با مدیر سامانه تماس بگیرید");
            }
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        showSnackbar("خطا در ورود به حساب کاربری!");
      });
  }

  const onCheckCaptcha = () => {
    setCheckCaptcha(checkCaptcha + 1);
  };

  const showSnackbar = (message) => {
    setSnack({
      ...snack,
      message: message
    });
    setSnackbarOpen(true);
  }

  return (
    <BasicLayout image={bgImage} isAuth>
      <Card className="sign-in">
        <MDBox
          variant="gradient"
          bgColor={AppConfig.color}
          borderRadius="lg"
          coloredShadow={AppConfig.color}
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("auth.login")}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <TextField id="user" label={t("user.user_id")} variant="outlined" fullWidth
                         value={user} autoFocus className="ltr-textfield" error={errors?.user} helperText={errors?.user}
                         onKeyPress={(e) => e.key === 'Enter' && inputRef.current.focus()}
                         onChange={(e) => handleChange(e.target.value, "user")} />
            </MDBox>

            <MDBox mb={2} style={{ direction: "ltr" }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel htmlFor="password" error={errors?.password} shrink>{t("auth.password")}</InputLabel>
                <OutlinedInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  inputRef={inputRef}
                  value={password}
                  error={errors?.password}
                  onChange={(e) => handleChange(e.target.value, "password")}
                  onKeyPress={(e) => e.key === 'Enter' && captchaRef.current.focus()}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t("auth.password")}
                  notched
                  className="ltr-password"
                />
                {errors?.password &&
                  <FormHelperText error>
                    {errors?.password}
                  </FormHelperText>
                }
              </FormControl>
            </MDBox>

            <MDBox mb={2}>
              <Captcha
                ref={captchaRef}
                errors={errors}
                setErrors={setErrors}
                setLoading={setLoading}
                checkCaptcha={checkCaptcha}
                loginFormCheck={checkInputs}
                login={login}
                showSnackbar={showSnackbar}
              />
            </MDBox>

            <MDBox mt={4} mb={1}>
              <LoadingButton
                id="sign-in-button"
                color={AppConfig.color}
                loading={loading}
                variant="contained"
                fullWidth
                onClick={onCheckCaptcha}
              >
                {t("auth.login")}
              </LoadingButton>
            </MDBox>

            <MDBox mt={3} mb={1} className="d-flex justify-content-center">
              <Link to={RouteUrls.auth.forgot_password} className="fp-link-style">
                {t("auth.forgot_password")}
              </Link>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

const mapDispatchToProps = dispatch => {
  return {
    setUserPermission: permission => dispatch(setUserPermission(permission))
  }
}

export default connect(null, mapDispatchToProps)(SignIn);
